import { useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CaptchaCheck from "@/components/checkout/CaptchaCheck";
import AccountBenefits from "@/components/checkout/AccountBenefits";
import { toast } from "sonner";

// Post-checkout inline sign-up (booking confirmation / payment success). Links a guest booking via its token.
export default function InlineAccountForm({ defaultEmail = "", defaultName = "", bookingToken = "", onCreated }) {
  const { setSession } = useAuth();
  const [first, ...rest] = (defaultName || "").trim().split(" ");
  const [f, setF] = useState({ first_name: first || "", last_name: rest.join(" "), email: defaultEmail, password: "" });
  const [terms, setTerms] = useState(false);
  const [cap, setCap] = useState({});
  const [capKey, setCapKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!terms) { setError("Please accept the Terms of Service to create your account."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/quick-register", {
        ...f, accepted_terms: true, booking_token: bookingToken, origin: window.location.origin,
        captcha_token: cap.token, captcha_answer: cap.answer, website: cap.website,
      });
      setSession(data);
      toast.success("Your account is ready.");
      onCreated && onCreated(data.user);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(formatApiError(d?.message ? d.message : d) || "We couldn't create your account.");
      setCapKey((k) => k + 1);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="text-left rounded-2xl border border-[#144F75]/20 bg-[#144F75]/[0.04] p-5 space-y-3" data-testid="inline-account-form">
      <p className="flex items-center gap-2 font-serif text-lg text-[#1A242B]"><UserPlus size={18} className="text-[#144F75]" /> Create your free account</p>
      <AccountBenefits />
      <div className="grid grid-cols-2 gap-2">
        <input required placeholder="First name" value={f.first_name} onChange={set("first_name")} data-testid="inline-account-first" className="eyw-input w-full" />
        <input required placeholder="Last name" value={f.last_name} onChange={set("last_name")} data-testid="inline-account-last" className="eyw-input w-full" />
      </div>
      <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="inline-account-email" className="eyw-input w-full" />
      <input required type="password" autoComplete="new-password" placeholder="Create a password (12+ characters, letters & numbers)" value={f.password} onChange={set("password")} data-testid="inline-account-password" className="eyw-input w-full" />
      <CaptchaCheck value={cap} onChange={setCap} refreshKey={capKey} testid="inline-account-captcha" />
      <label className="flex items-start gap-2 text-xs text-[#4A5D6B]">
        <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="accent-[#144F75] mt-0.5" data-testid="inline-account-terms" />
        <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-[#144F75] underline">Terms</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#144F75] underline">Privacy Policy</a>.</span>
      </label>
      {error && <p className="text-sm text-red-600" data-testid="inline-account-error">{error}</p>}
      <button type="submit" disabled={busy} data-testid="inline-account-submit" className="eyw-btn w-full justify-center disabled:opacity-60">
        {busy && <Loader2 size={16} className="animate-spin" />} Create account
      </button>
    </form>
  );
}
